import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, apiJson } from "../api/client";
import { useLocale } from "../contexts/LocaleContext";
import { useToast } from "../contexts/ToastContext";

// Profile-completion form shown when a logged-in user hasn't accepted the
// ToS yet (`data.tos === null` on the Google verify response). Mirrors the
// fields of the legacy register.html but as a controlled React form.

interface UserResponse {
  name?: string;
  email?: string;
  [k: string]: unknown;
}

const COUNTRY_CODES = [
  "AT",
  "DE",
  "CH",
  "AF",
  "AL",
  "DZ",
  "AD",
  "AO",
  "AG",
  "AR",
  "AM",
  "AZ",
  "AU",
  "BS",
  "BH",
  "BD",
  "BB",
  "BY",
  "BE",
  "BZ",
  "BJ",
  "BT",
  "BO",
  "BA",
  "BW",
  "BR",
  "BN",
  "BG",
  "BF",
  "BI",
  "CV",
  "CL",
  "CN",
  "CR",
  "CD",
  "DM",
  "DO",
  "DJ",
  "DK",
  "EC",
  "SV",
  "CI",
  "ER",
  "EE",
  "SZ",
  "FJ",
  "FI",
  "FR",
  "GA",
  "GM",
  "GE",
  "GH",
  "GD",
  "GR",
  "GT",
  "GN",
  "GW",
  "GY",
  "HT",
  "HN",
  "IN",
  "ID",
  "IQ",
  "IR",
  "IE",
  "IS",
  "IL",
  "IT",
  "JM",
  "JP",
  "YE",
  "JO",
  "KH",
  "CM",
  "CA",
  "KZ",
  "QA",
  "KE",
  "KG",
  "KI",
  "CO",
  "KM",
  "HR",
  "CU",
  "KW",
  "LA",
  "LS",
  "LV",
  "LB",
  "LR",
  "LY",
  "LI",
  "LT",
  "LU",
  "MG",
  "MW",
  "MY",
  "MV",
  "ML",
  "MT",
  "MA",
  "MH",
  "MR",
  "MU",
  "MX",
  "FM",
  "MD",
  "MC",
  "MN",
  "ME",
  "MZ",
  "MM",
  "NA",
  "NR",
  "NP",
  "NZ",
  "NI",
  "NL",
  "NE",
  "NG",
  "KP",
  "MK",
  "NO",
  "OM",
  "PK",
  "PW",
  "PS",
  "PA",
  "PG",
  "PY",
  "PE",
  "PH",
  "PL",
  "PT",
  "CG",
  "RW",
  "RO",
  "RU",
  "SB",
  "ZM",
  "WS",
  "SM",
  "SA",
  "SE",
  "SN",
  "RS",
  "SC",
  "SL",
  "ZW",
  "SG",
  "SK",
  "SI",
  "SO",
  "ES",
  "LK",
  "KN",
  "LC",
  "VC",
  "SD",
  "SR",
  "SY",
  "ST",
  "ZA",
  "KR",
  "SS",
  "TJ",
  "TW",
  "TZ",
  "TH",
  "TL",
  "TG",
  "TO",
  "TT",
  "TD",
  "CZ",
  "TN",
  "TM",
  "TV",
  "TR",
  "UG",
  "UA",
  "HU",
  "UY",
  "UZ",
  "VU",
  "VA",
  "VE",
  "AE",
  "US",
  "GB",
  "VN",
  "CF",
  "CY",
  "EG",
  "GQ",
  "ET",
] as const;

type CountryCode = (typeof COUNTRY_CODES)[number];

export function RegisterPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useLocale();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [land, setLand] = useState<CountryCode | "">("");
  const [city, setCity] = useState("");
  const [isArtist, setIsArtist] = useState(false);
  const [artistType, setArtistType] = useState("");
  const [otherArtistType, setOtherArtistType] = useState("");
  const [donationCert, setDonationCert] = useState(false);
  const [donationFirstName, setDonationFirstName] = useState("");
  const [donationLastName, setDonationLastName] = useState("");
  const [donationAddress, setDonationAddress] = useState("");
  const [donationPostalCode, setDonationPostalCode] = useState("");
  const [donationCity, setDonationCity] = useState("");
  const [donationCountry, setDonationCountry] = useState<CountryCode>("AT");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState("");
  const [tos, setTos] = useState(false);

  // Prefill name/email from /api/v1/user (Google profile data the backend
  // already has). If not signed in, bounce to login.
  useEffect(() => {
    let cancelled = false;
    apiFetch("/user")
      .then(async (r) => {
        if (r.status === 401) {
          if (!cancelled) navigate("/login", { replace: true });
          return null;
        }
        if (!r.ok) return null;
        return (await r.json()) as UserResponse;
      })
      .then((data) => {
        if (!data || cancelled) return;
        if (data.name) setName(String(data.name));
        if (data.email) setEmail(String(data.email));
      })
      .catch((err) => console.error("Error fetching user data:", err));
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!tos) {
      toast.show(t("register.tosRequired"), "danger");
      return;
    }
    try {
      await apiJson("/user", {
        method: "POST",
        body: JSON.stringify({
          state: land,
          city,
          isArtist,
          artistType,
          otherArtistType,
          donationCert,
          donationFirstName,
          donationLastName,
          donationAddress,
          donationPostalCode,
          donationCity,
          donationCountry,
          birthdate,
          gender,
          tos,
        }),
      });
      toast.show(t("register.success"), "success");
      const redirect = sessionStorage.getItem("postLoginRedirect");
      sessionStorage.removeItem("postLoginRedirect");
      navigate(redirect ? `/${redirect}` : "/profile", { replace: true });
    } catch (err) {
      console.error("Error during registration:", err);
      toast.show(t("register.failed"), "danger");
    }
  };

  return (
    <section className="content">
      <h1>{t("register.title")}</h1>
      <p className="centered">
        {t("register.alreadyRegistered")}{" "}
        <Link to="/login">{t("register.loginLink")}</Link>.
      </p>

      <form className="custum-form" onSubmit={onSubmit}>
        <div>
          <label>
            {t("register.artistName")}
            <br />
            <input
              type="text"
              name="vorname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              readOnly
            />
          </label>
        </div>

        <div>
          <label>
            {t("register.email")}
            <br />
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              readOnly
            />
          </label>
        </div>

        <div>
          <label>
            {t("register.country")}
            <br />
            <select
              name="land"
              value={land}
              onChange={(e) =>
                setLand(e.target.value as CountryCode | "")
              }
              required
            >
              <option value="" disabled>
                {t("register.selectCountry")}
              </option>
              {COUNTRY_CODES.map((countryCode) => (
                <option key={countryCode} value={countryCode}>
                  {t(`register.countries.${countryCode}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <label>
            {t("register.city")}
            <br />
            <input
              type="text"
              name="adresse"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </label>
        </div>

        <div className="block">
          <label>
            {t("register.isArtist")}
            <input
              type="checkbox"
              checked={isArtist}
              onChange={(e) => setIsArtist(e.target.checked)}
            />
          </label>

          {isArtist && (
            <div id="artistFields">
              <div>
                <label>
                  {t("register.artistType")}
                  <br />
                  <select
                    name="art"
                    value={artistType}
                    onChange={(e) => setArtistType(e.target.value)}
                  >
                    <option value="">{t("register.selectArtistType")}</option>
                    <option value="Maler">{t("register.painter")}</option>
                    <option value="Bildhauer">{t("register.sculptor")}</option>
                    <option value="Fotograf">{t("register.photographer")}</option>
                    <option value="Sonstiges">{t("register.other")}</option>
                  </select>
                </label>
              </div>
              {artistType === "Sonstiges" && (
                <div>
                  <label>
                    {t("register.otherSpecify")}
                    <br />
                    <input
                      type="text"
                      name="sonstiges"
                      value={otherArtistType}
                      onChange={(e) => setOtherArtistType(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          <label>
            {t("register.donationCert")}
            <input
              type="checkbox"
              checked={donationCert}
              onChange={(e) => setDonationCert(e.target.checked)}
            />
          </label>

          {donationCert && (
            <div id="donationFields" style={{ display: "flex", flexWrap: "wrap" }}>
              <div>
                <label>
                  {t("register.firstName")}
                  <br />
                  <input
                    type="text"
                    value={donationFirstName}
                    onChange={(e) => setDonationFirstName(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.lastName")}
                  <br />
                  <input
                    type="text"
                    value={donationLastName}
                    onChange={(e) => setDonationLastName(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.address")}
                  <br />
                  <input
                    type="text"
                    value={donationAddress}
                    onChange={(e) => setDonationAddress(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.postalCode")}
                  <br />
                  <input
                    type="text"
                    value={donationPostalCode}
                    onChange={(e) => setDonationPostalCode(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.city")}
                  <br />
                  <input
                    type="text"
                    value={donationCity}
                    onChange={(e) => setDonationCity(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.country")}
                  <br />
                  <select
                    value={donationCountry}
                    onChange={(e) =>
                      setDonationCountry(e.target.value as CountryCode)
                    }
                  >
                    {COUNTRY_CODES.map((countryCode) => (
                      <option key={countryCode} value={countryCode}>
                        {t(`register.countries.${countryCode}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>

        <div>
          <label>
            {t("register.birthdate")}
            <br />
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
            />
          </label>
        </div>

        <div>
          <label>
            {t("register.gender")}
            <br />
            <select value={gender} onChange={(e) => setGender(e.target.value)}>
              <option value="">{t("register.genderNone")}</option>
              <option value="m">{t("register.genderMale")}</option>
              <option value="w">{t("register.genderFemale")}</option>
              <option value="d">{t("register.genderDiverse")}</option>
            </select>
          </label>
        </div>

        <label>
          {t("register.tos").split(/\b(?:AGB|Terms & Conditions)\b/)[0]}
          <Link to="/agb">{t("bottomline.agb")}</Link>
          {t("register.tos").split(/\b(?:AGB|Terms & Conditions)\b/)[1]}
          <input
            type="checkbox"
            checked={tos}
            onChange={(e) => setTos(e.target.checked)}
          />
        </label>

        <div style={{ gridColumn: "span 2" }}>
          <button type="submit">{t("register.submit")}</button>
        </div>
      </form>
    </section>
  );
}
